import moment from 'moment';
import {Params} from '@angular/router';
import {InvoiceIssue} from '../interfaces/invoicing';
import {companyTabOf} from '../clients/company-tab/company-tabs';

// Pure resolver (#1518 PR8b): turns a backend issue's targets into one permitted action, or
// 'admin' when every target exists but none is permitted for this viewer, or null when the
// issue has no target at all (text only). No side effects, no Angular DI: it is unit tested
// as a table, not through the component.
export interface IssueViewer {
  id: number;
  activeAdmin: boolean;
  staffer: boolean;
  // Company billing/VAT tabs are reachable (companyTabAllowed('billing'|'vat', ...)); profile
  // is always reachable to an active Admin regardless.
  companyTabs: boolean;
}

export interface IssueAction {
  link?: any[];
  queryParams?: Params;
  local?: {line_id?: number, delete?: true};
  label: string;
  params: object;
}

function monday(week_start: string): string {
  return moment(week_start).startOf('isoWeek').format('YYYY-MM-DD');
}

function isoWeek(date: string): number {
  return moment(date).isoWeek();
}

export function resolveIssue(issue: InvoiceIssue, viewer: IssueViewer, ctx: {project_id: number}): IssueAction | IssueAction[] | 'admin' | null {
  if (!issue.targets.length) { return null; }

  // line.default_rate is the one code with two independently permitted actions (confirm the
  // price on this line, or set the person's project rate so it is not needed again): every
  // other code keeps first-target-wins below.
  if (issue.code === 'line.default_rate') {
    const actions: IssueAction[] = [];
    for (const t of issue.targets) {
      if (t.surface === 'invoice.line') {
        actions.push({local: {line_id: t.line_id}, label: 'action.confirm_price', params: {}});
      } else if (t.surface === 'project.members' && (viewer.activeAdmin || viewer.staffer)) {
        actions.push({link: ['/projects', t.project_id], queryParams: {edit: 1, field: 'members'}, label: 'action.set_rate', params: {person: issue.params.person}});
      }
    }
    return actions.length ? actions : 'admin';
  }

  // C2 (PR8b fix round 3): project.client_reference has two independently permitted targets
  // (the project's own field, or the client supplier code the UBL export falls back to). The
  // generic first-target-wins loop below made the client target permanently unreachable: its
  // 'project' branch only needs viewer.activeAdmin and is always checked first, so the 'client'
  // branch below never got a turn, admin or not. Resolve both like line.default_rate instead.
  if (issue.code === 'project.client_reference') {
    const actions: IssueAction[] = [];
    for (const t of issue.targets) {
      if (t.surface === 'project' && viewer.activeAdmin) {
        actions.push({link: ['/projects', t.project_id], queryParams: {field: t.field}, label: 'fix.project.' + t.field, params: {}});
      } else if (t.surface === 'client' && viewer.activeAdmin && viewer.companyTabs) {
        actions.push({link: ['/clients', t.client_id], queryParams: {field: t.field}, label: 'fix.client.' + t.field, params: {}});
      }
    }
    return actions.length ? actions : 'admin';
  }

  let sawTarget = false;
  for (const t of issue.targets) {
    sawTarget = true;
    switch (t.surface) {
      case 'company': {
        const tab = companyTabOf(t.field) ?? 'profile';
        if (viewer.activeAdmin && (tab === 'profile' || viewer.companyTabs)) {
          return {link: ['/company', tab], queryParams: {field: t.field}, label: 'fix.company.' + t.field, params: {}};
        }
        break;
      }
      case 'client': {
        if (viewer.activeAdmin && viewer.companyTabs) {
          const key = issue.code === 'client.no_einvoice' ? 'action.add_peppol' : 'fix.client.' + t.field;
          return {link: ['/clients', t.client_id], queryParams: {field: t.field}, label: key, params: {}};
        }
        break;
      }
      case 'project': {
        if (viewer.activeAdmin) {
          return {link: ['/projects', t.project_id], queryParams: {field: t.field}, label: 'fix.project.' + t.field, params: {}};
        }
        break;
      }
      case 'project.members': {
        if (viewer.activeAdmin || viewer.staffer) {
          return {link: ['/projects', t.project_id], queryParams: {edit: 1, field: 'members'}, label: 'action.set_rate', params: {person: issue.params.person}};
        }
        break;
      }
      case 'project.invoices': {
        return {link: ['/projects', t.project_id], queryParams: {field: 'invoices'}, label: 'action.make_draft', params: {}};
      }
      case 'invoice.line': {
        return {local: {line_id: t.line_id}, label: issue.code === 'line.default_rate' ? 'action.confirm_price' : 'action.price_below', params: {}};
      }
      case 'invoice.delete': {
        return {local: {delete: true}, label: 'action.delete_draft', params: {}};
      }
      case 'approval': {
        if (viewer.activeAdmin || viewer.staffer) {
          const path = t.status === 'submitted' ? '/time-approvals/pending' : '/time-approvals/approved';
          return {link: [path], queryParams: {request: t.request_id}, label: 'action.review_week', params: {week: isoWeek(issue.params.week_start)}};
        }
        break;
      }
      case 'registration.week': {
        const self = viewer.id === t.user_id;
        if (self || viewer.activeAdmin || viewer.staffer) {
          const queryParams: Params = {week: monday(t.week_start)};
          if (!self) { queryParams.user = t.user_id; }
          return {link: ['/registration/hours/calendar'], queryParams, label: self ? 'action.view_week' : 'action.open_week',
            params: {person: issue.params.person, week: isoWeek(t.week_start)}};
        }
        break;
      }
      default:
        break;
    }
  }
  return sawTarget ? 'admin' : null;
}

export function groupIssues(issues: InvoiceIssue[]): {blocking: InvoiceIssue[], review: InvoiceIssue[], info: InvoiceIssue[]} {
  return {
    blocking: issues.filter(i => i.severity === 'blocking'),
    review: issues.filter(i => i.severity === 'review'),
    info: issues.filter(i => i.severity === 'info'),
  };
}
